import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import Footer from '../Footer';
import Header from '../Header';

// Use hardcoded paths, not dependent on environment variables
const DOWNLOAD_BASE_URL = '/downloads';
const FILE_PATHS = {
  'mac-intel': {
    url: `/downloads/AIMeetingPro-Intel.dmg`,
    fileName: 'AIMeetingPro-Intel.dmg'
  },
  'mac-silicon': {
    url: `/downloads/AIMeetingPro-AppleSilicon.dmg`,
    fileName: 'AIMeetingPro-AppleSilicon.dmg'
  },
  'windows': {
    url: `/downloads/AIMeetingPro-Setup.exe`,
    fileName: 'AIMeetingPro-Setup.exe'
  },
  'linux': {
    url: `/downloads/AIMeetingPro.AppImage`,
    fileName: 'AIMeetingPro.AppImage'
  }
};

// App version and release date - hardcoded
const APP_VERSION = '1.2.3';
const RELEASE_DATE = 'June 15, 2023';

const AIMeetingProDownload = () => {
  const [os, setOs] = useState('mac-intel'); // Default to Intel Mac
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [envDebugInfo, setEnvDebugInfo] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Set environment variable debug info
    setEnvDebugInfo({
      // Environment variable info
      NODE_ENV: process.env.NODE_ENV,
      PUBLIC_URL: process.env.PUBLIC_URL,
      
      // Hardcoded configuration info
      DOWNLOAD_BASE_URL,
      FILE_PATHS,
      CURRENT_FILE_PATH: FILE_PATHS[os],
      APP_VERSION,
      RELEASE_DATE
    });
    
    // Detect user's operating system only on initial load
    const userAgent = window.navigator.userAgent;
    console.log('User Agent:', userAgent);
    console.log('Detected OS will be set to default mac-intel for demo purposes');
    
    // For demo purposes, we'll keep the default as mac-intel
    // In a real application, you might want to detect the actual OS
    /*
    if (userAgent.indexOf("Windows") !== -1) setOs('windows');
    else if (userAgent.indexOf("Mac") !== -1) {
      // Detect if it's Apple Silicon Mac
      if (navigator.userAgentData && navigator.userAgentData.platform === 'macOS' && 
          /Apple/.test(navigator.hardwareConcurrency)) {
        setOs('mac-silicon');
      } else {
        setOs('mac-intel');
      }
    }
    else if (userAgent.indexOf("Linux") !== -1) setOs('linux');
    */
  }, []); // Remove 'os' from dependency array to prevent infinite loop

  // Function to navigate to specific sections on the main page
  const navigateToSection = (sectionId) => {
    navigate('/', { state: { scrollTo: sectionId } });
  };

  const startDownload = () => {
    try {
      setDownloadStarted(true);
      setDownloadError(null);
      
      const fileInfo = FILE_PATHS[os];
      console.log('Starting file download:', fileInfo);
      
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      
      // Use hardcoded path
      const downloadUrl = fileInfo.url;
      console.log('Download URL:', downloadUrl);
      
      link.href = downloadUrl;
      link.setAttribute('download', fileInfo.fileName);
      link.setAttribute('target', '_blank');
      
      // Add to document and click to trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Reset download status after 5 seconds
      setTimeout(() => {
        setDownloadStarted(false);
      }, 5000);
      
      // Log download event
      console.log(`Download completed: ${os} version`);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(`Download failed: ${error.message || 'Unknown error'}`);
      setDownloadStarted(false);
    }
  };

  // Add error prompt in download button section
  const renderDownloadButton = () => {
    // Get file size info (using fixed values here, real applications should get from server)
    const fileSizes = {
      'mac-intel': '101 MB',
      'mac-silicon': '96 MB',
      'windows': '64 MB',
      'linux': '68 MB'
    };
    
    const fileSize = fileSizes[os] || '';
    const buttonText = downloadStarted 
      ? 'Downloading...' 
      : `Download ${os === 'mac-intel' ? 'Intel Mac' : os === 'mac-silicon' ? 'Apple Silicon Mac' : os === 'windows' ? 'Windows' : 'Linux'} Version${fileSize ? ` (${fileSize})` : ''}`;
    
    return (
      <>
        <button 
          className={`download-button ${downloadStarted ? 'downloading' : ''}`} 
          onClick={startDownload}
          disabled={downloadStarted}
        >
          {buttonText}
        </button>
        {downloadError && <div className="download-error">{downloadError}</div>}
      </>
    );
  };

  // Render version info component
  const renderVersionInfo = () => {
    return (
      <div className="version-info">Version {APP_VERSION} | Release Date: {RELEASE_DATE}</div>
    );
  };

  return (
    <div className="download-container">
      <Header />

      <main className="download-main">
        <div className="download-hero">
          <h1>Download AI Meeting Pro</h1>
          <p>Get the desktop application to practice interviews anytime, anywhere - even offline.</p>
          
          <div className="system-selector">
            
            {/* <button 
              className={os === 'windows' ? 'active' : ''} 
              onClick={() => setOs('windows')}
            >
              <span className="system-icon">🪟</span>
              Windows
            </button> */}
            <button 
              className={os === 'mac-intel' ? 'active' : ''} 
              onClick={() => {
                console.log('Clicked Mac Intel, setting OS to mac-intel');
                setOs('mac-intel');
              }}
            >
              <span className="system-icon">🍎</span>
              Mac (Intel)
            </button>
            <button 
              className={os === 'mac-silicon' ? 'active' : ''} 
              onClick={() => {
                console.log('Clicked Mac Silicon, setting OS to mac-silicon');
                setOs('mac-silicon');
              }}
            >
              <span className="system-icon">🍎</span>
              Mac (Apple Silicon)
            </button>
            {/* <button 
              className={os === 'linux' ? 'active' : ''} 
              onClick={() => setOs('linux')}
            >
              <span className="system-icon">🐧</span>
              Linux
            </button> */}
          </div>
          
          <div className="download-info">
            {os === 'windows' && (
              <div className="os-info">
                <h2>Windows Version</h2>
                <p>Requires Windows 10 or newer</p>
                <ul className="requirements">
                  <li>At least 4GB RAM</li>
                  <li>1GB available disk space</li>
                  <li>DirectX 11 compatible graphics card</li>
                </ul>
                {renderVersionInfo()}
                {renderDownloadButton()}
              </div>
            )}
            
            {os === 'mac-intel' && (
              <div className="os-info">
                <h2>macOS Version (Intel)</h2>
                <p>For Mac computers with Intel processors</p>
                <ul className="requirements">
                  <li>macOS 11.0 (Big Sur) or higher</li>
                  <li>At least 4GB RAM</li>
                  <li>1.5GB available disk space</li>
                  <li>Intel processor</li>
                </ul>
                {renderVersionInfo()}
                {renderDownloadButton()}
              </div>
            )}
            
            {os === 'mac-silicon' && (
              <div className="os-info">
                <h2>macOS Version (Apple Silicon)</h2>
                <p>Optimized for M1/M2/M3 Mac computers</p>
                <ul className="requirements">
                  <li>macOS 11.0 (Big Sur) or higher</li>
                  <li>At least 4GB RAM</li>
                  <li>1.5GB available disk space</li>
                  <li>Apple Silicon processor (M1/M2/M3)</li>
                </ul>
                {renderVersionInfo()}
                {renderDownloadButton()}
              </div>
            )}
            
            {os === 'linux' && (
              <div className="os-info">
                <h2>Linux Version</h2>
                <p>Compatible with most major distributions</p>
                <ul className="requirements">
                  <li>At least 4GB RAM</li>
                  <li>1GB available disk space</li>
                  <li>x86_64 architecture</li>
                </ul>
                {renderVersionInfo()}
                {renderDownloadButton()}
              </div>
            )}
          </div>
        </div>
        
        <div className="installation-guide">
          <h2>Installation Guide</h2>
          
          {os === 'windows' && (
            <ol className="install-steps">
              <li>Download the installer (.exe file)</li>
              <li>Double-click to run the installer</li>
              <li>Follow the on-screen instructions</li>
              <li>Launch AI Meeting Pro from Start menu or desktop shortcut</li>
            </ol>
          )}
          
          {(os === 'mac-intel' || os === 'mac-silicon') && (
            <ol className="install-steps">
              <li>Download the disk image (.dmg file)</li>
              <li>Double-click to open the disk image</li>
              <li>Drag the AI Meeting Pro icon to Applications folder</li>
              <li>Launch AI Meeting Pro from Applications folder or Dock</li>
            </ol>
          )}
          
          {os === 'linux' && (
            <ol className="install-steps">
              <li>Download the AppImage file</li>
              <li>Make the file executable: <code>chmod +x AI-Meeting-Pro.AppImage</code></li>
              <li>Run the application: <code>./AI-Meeting-Pro.AppImage</code></li>
              <li>Optional: Integrate it into your desktop environment</li>
            </ol>
          )}
        </div>
        
        {/* <div className="other-options">
          <h2>Other Download Options</h2>
          <div className="options-grid">
            <div className="option-card">
              <h3>Source Code</h3>
              <p>Access the open source repository on GitHub</p>
              <a href="https://github.com/ai-meeting-pro" className="github-link" target="_blank" rel="noopener noreferrer">
                View Source Code
              </a>
            </div>
            <div className="option-card">
              <h3>Previous Versions</h3>
              <p>Download older versions of AI Meeting Pro</p>
              <a href="/download/archive" className="archive-link">
                View Archive
              </a>
            </div>
            <div className="option-card">
              <h3>Mobile Versions</h3>
              <p>Get AI Meeting Pro for iOS and Android</p>
              <div className="mobile-links">
                <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">App Store</a>
                <a href="https://play.google.com" target="_blank" rel="noopener noreferrer">Google Play</a>
              </div>
            </div>
          </div>
        </div> */}
      </main>
      
      <Footer />
    </div>
  );
};

export default AIMeetingProDownload;